import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  FieldType,
  FormlyFieldConfig,
  FormlyFieldProps,
} from '@ngx-formly/core';
import { TextareaModule } from 'primeng/textarea';

interface TextareaProps extends FormlyFieldProps {
  rows?: number;
  cols?: number;
  autoResize?: boolean;
  autoResizeMaxHeight?: number;
}

export interface FormlyTextareaFieldConfig
  extends FormlyFieldConfig<TextareaProps> {}

@Component({
  selector: 'kon-textarea-type',
  imports: [ReactiveFormsModule, TextareaModule],
  template: `<textarea
    [rows]="props.rows"
    [cols]="props.cols"
    pTextarea
    [formControl]="$any(formControl)"
    [readOnly]="props.readonly"
    fluid
    #textArea
    (dblclick)="onDblClick($event)"
  ></textarea>`,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyTextareaTypeComponent
  extends FieldType<FormlyTextareaFieldConfig>
  implements AfterViewInit
{
  private readonly destroyRef = inject(DestroyRef);

  override defaultOptions?: Partial<FormlyTextareaFieldConfig> | undefined = {
    props: {
      autoResize: true,
      autoResizeMaxHeight: 300,
    },
  };

  @ViewChild('textArea', { static: true })
  textArea!: ElementRef<HTMLTextAreaElement>;

  ngAfterViewInit() {
    requestAnimationFrame(() => this.resizeToContent());

    this.formControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (!value) {
          this.textArea.nativeElement.style.height = 'auto';
        }

        this.resizeToContent();
      });
  }

  onDblClick(event: MouseEvent) {
    // Hacky way to check if the user double-clicked on the resize handle of the textarea.
    // This is not a perfect solution, but it works for most cases.
    const textarea = event.target as HTMLTextAreaElement;
    const rect = textarea.getBoundingClientRect();
    const handleSize = 16;
    const onHandle =
      event.clientX >= rect.right - handleSize &&
      event.clientY >= rect.bottom - handleSize;

    if (onHandle) {
      this.resizeToContent(true);
    }
  }

  resizeToContent(ignoreLimits = false) {
    if (!this.props.autoResize || !this.textArea) {
      return;
    }

    const textarea = this.textArea.nativeElement;
    const maxHeight = this.props.autoResizeMaxHeight ?? 300;
    const currentHeight = textarea.offsetHeight;

    if (currentHeight >= maxHeight && !ignoreLimits) {
      return;
    }

    textarea.style.height = 'auto';

    const newHeight = ignoreLimits
      ? textarea.scrollHeight
      : Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = `${newHeight}px`;
  }
}
